import axios, { InternalAxiosRequestConfig } from "axios";
import { isTokenValid } from "../../utils/token";
//import { config } from "../../config/environment";

// Define public endpoints that don't need authentication
const PUBLIC_ENDPOINTS = [
  "/auth/login",
  "/auth",
  "/doctors",
  "/doctors/", // For dynamic routes like /doctors/123
  "/articles",
  "/articles/",
  "/managedservices",
  "/managedservices/",
  "/appointments",
  "/appointments/",
  "/clinics",
  "/clinics/",
  "/testimonials",
  "/testimonials/",
];

// Custom config type to include requireAuth flag
interface CustomRequestConfig extends InternalAxiosRequestConfig {
  requireAuth?: boolean;
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Helper function to check if endpoint is public
const isPublicEndpoint = (url: string | undefined) => {
  if (!url) return false;
  return PUBLIC_ENDPOINTS.some((endpoint) => url.startsWith(endpoint));
};

// Request interceptor to add token for authenticated endpoints
api.interceptors.request.use((config: CustomRequestConfig) => {
  const token = localStorage.getItem("token");
  const url = config.url?.replace(config.baseURL || "", "");

  // Add auth header if:
  // 1. requireAuth is explicitly true, OR
  // 2. requireAuth is not set AND endpoint is not public
  const needsAuth = config.requireAuth ?? !isPublicEndpoint(url);

  if (needsAuth && token && isTokenValid(token)) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only handle 401 errors for authenticated endpoints
    if (error.response?.status === 401) {
      const config = error.config as CustomRequestConfig;
      const url = config.url?.replace(config.baseURL || "", "");
      const needsAuth = config.requireAuth ?? !isPublicEndpoint(url);

      if (needsAuth) {
        localStorage.removeItem("token");
        if (!window.location.pathname.includes("/auth/signin")) {
          window.location.href = `/auth/signin?redirect=${window.location.pathname}`;
        }
      }
    }
    return Promise.reject(error);
  }
);
